/** ClampedBlock — collapse long content to a height cap with a Show more / Show less toggle.
 *
 * ONE implementation for three surfaces that all render the same qualitative blocks (an accepted
 * Progress / New-Info run can be twenty bullets long): the Recent-Updates cluster, the record
 * timeline's rows, and any other panel section that needs a body kept to a readable height. The
 * clamp is the same reason `CrmBlockRuns` exists — three copies of a collapse rule drift, and here
 * they would drift into three different heights on one screen.
 *
 * ⛔ THE TOGGLE IS MEASURED, NEVER ASSUMED. It renders only when the content actually exceeds the cap:
 * a "Show more" under three bullets that are already fully visible is a control that does nothing, and
 * on a governed surface a dead expander reads as "something is being withheld". Measurement is
 * `scrollHeight > cap` on the content element, re-taken when the content resizes.
 *
 * ⚠ Consequence in jsdom, stated so nobody debugs it twice: layout is not computed there, so
 * `scrollHeight` is 0 and NO toggle renders unless a test stubs it. That is the right default — tests
 * that don't care about clamping see the full content, exactly as before this component existed.
 *
 * Nothing here is URL state: expanding a row is an act, not a place (same rule as the timeline's
 * evidence-body expander), so it must not ride in a shared link.
 *
 * ⛔ THE CALLER MUST GIVE THIS A REACT `key` TIED TO THE CONTENT'S IDENTITY. `expanded` lives in this
 * component, and the panels that host it do NOT remount when the record changes: a ‹ › hop swaps the
 * `children` of the SAME instance. Without a key, the block that the operator expanded on Edko opens
 * already-expanded on the next deal — one record's act re-applied to another record's content, which
 * is the same "expanding is an act, not a place" rule read the other way round. On a governed surface
 * that is worse than untidy: the expanded state is the operator's assertion that they have read THIS
 * text, and it must not silently transfer.
 *
 * The key is the identity the content already has: the timeline row's `it.key`, and `entryId` for the
 * Recent-Updates cluster. Deliberately NOT a new prop — a `resetKey` here would be a second, weaker
 * spelling of something React already does exactly (a key remounts, so the measurement is retaken for
 * the new content too, which a state reset alone would not guarantee). The test file pins both halves:
 * the failure mode without a key, and the reset with one.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import styles from './ClampedBlock.module.css';

interface Props {
  children: React.ReactNode;
  /** Collapsed height cap in px. Callers pass their surface's own reading height. */
  maxHeight?: number;
  /** What the toggle is expanding, for the screen-reader label ("Show more of Deal Progress"). */
  label?: string;
  className?: string;
}

const DEFAULT_MAX_HEIGHT = 160;

export function ClampedBlock({ children, maxHeight = DEFAULT_MAX_HEIGHT, label, className }: Props) {
  const inner = useRef<HTMLDivElement | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  const measure = useCallback(() => {
    const el = inner.current;
    if (!el) return;
    // Measured against the CONTENT element, which is never itself clamped (the wrapper is), so this
    // reading does not change when `expanded` flips — otherwise collapsing would re-measure the
    // clamped height, conclude "fits", and drop the control that got you back.
    setOverflows(el.scrollHeight > maxHeight + 1);
  }, [maxHeight]);

  useEffect(() => {
    measure();
    if (typeof ResizeObserver === 'undefined') return;   // jsdom / old browsers: one measurement
    const el = inner.current;
    if (!el) return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure, children]);

  const collapsed = overflows && !expanded;

  return (
    <div className={className}>
      <div
        className={collapsed ? styles.clamped : undefined}
        style={collapsed ? { maxHeight } : undefined}
        data-clamped={collapsed ? 'true' : 'false'}
      >
        <div ref={inner}>{children}</div>
      </div>
      {overflows && (
        <button
          type="button"
          className={styles.toggle}
          data-clamp-toggle={label ?? ''}
          aria-expanded={expanded}
          aria-label={label ? `${expanded ? 'Show less of' : 'Show more of'} ${label}` : undefined}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

export default ClampedBlock;
